/**
 * ProductQuickCreateDialog - Minimal dialog for quick product creation
 *
 * Following the Quick Create Dialog pattern:
 * - 3-5 fields maximum (Name, Type, Category)
 * - Redirects to detail page after creation for full editing
 *
 * Progressive disclosure (mirrors OrganizationQuickCreateDialog): the dialog
 * opens on an "Import from the web" card — paste a product-page URL and the AI
 * extracts the product details, prefilling the manual fields (and carrying extra
 * enriched fields + the product image into the create payload). The user can skip
 * straight to manual entry.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { AlertCircle, Loader2, RefreshCw, Sparkles } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { Alert, AlertDescription, AlertTitle } from '../../../../components/ui/alert';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { UomSelect } from '../../../../shared/components/common/UomSelect';
import { isUomToken } from '../../../../shared/utils/uom';
import type { Uom } from '../../../../generated/config-enums/uom';
import { Button } from '../../../../components/ui/button';
import { Skeleton } from '../../../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { AIProcessingBanner, DialogForm, InfoTooltip } from '../../../../shared/components/common';
import {
  CustomFieldsSection,
  activeCustomFieldDefinitions,
  useCustomFieldDefinitions,
  validateCustomFields,
  type CustomFieldErrors,
} from '../../../../shared/components/customFields';
import { getFieldErrors } from '../../../../shared/utils/errors';
import { ToggleGroup, ToggleGroupItem } from '../../../../components/ui/toggle-group';
import { PRODUCT_ROLE_VALUES } from '../../../../generated/config-enums';
import {
  computeAvailableProductTypes,
  isSellTypeRestricted,
} from '../productTypeOptions';
import { useEmbeddingPrefix } from '../../../../shared/hooks';
import { useProgressStream } from '../../../../shared/hooks/useProgressStream';
import { fileApi } from '../../../../shared/files/services/fileApi';
import { useCreateProduct } from '../hooks/useProducts';
import { useCreateVendorSku } from '../hooks';
import { useProductCapabilities } from '../hooks/useSettings';
import { ProductCategoryPicker } from './ProductCategoryPicker';
import { productRoleLabel } from './ProductRoleBadge';
import type { Product, ProductRole } from '../types';

interface ProductQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Callback when product is created (before navigation) */
  onCreated?: (product: Product) => void;
  /** If true, don't navigate to detail page after creation */
  skipNavigation?: boolean;
  /** Pre-fill the name field (e.g. the picker search text the user had typed). */
  defaultName?: string;
  /**
   * Pre-select the Sell/Buy/Both role. Manual catalogue additions default to
   * 'sell'; a create opened from a purchase context (e.g. a vendor bill import)
   * passes 'buy'.
   */
  defaultRole?: ProductRole;
  /**
   * When set, the created product is linked to this organization as a supplier
   * (a vendor SKU) so it appears pre-filled in the product's Sourcing tab. Used
   * by the bill import: the bill's matched vendor seeds the new product's
   * sourcing.
   */
  defaultVendorId?: string;
  /** Display name of {@link defaultVendorId}, shown in the "will be linked" hint. */
  defaultVendorName?: string;
}

interface QuickCreateFormData {
  name: string;
  product_type: 'physical' | 'service' | 'subscription';
  /** Buy/sell/both. Manual catalogue additions default to 'sell'. */
  product_role: ProductRole;
  base_unit_name: string;
  /** Base unit token — fixes whether a quantity of this product may be fractional. */
  base_uom: Uom;
  category_id?: string;
}

/** Fields the URL import streams back; the editable form fields are prefilled and
 * the rest ride along into the create payload. Mirrors the backend
 * ProductUrlEnrichmentResponse. */
interface ProductUrlEnrichment {
  name?: string | null;
  brand?: string | null;
  description?: string | null;
  product_type?: string | null;
  base_unit_name?: string | null;
  sku?: string | null;
  upc?: string | null;
  gtin?: string | null;
  hs_code?: string | null;
  weight?: number | null;
  weight_unit?: string | null;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  dimension_unit?: string | null;
  image_url?: string | null;
}

const ENRICH_URL = '/api/v1/master-data/products/enrich-from-url';

/** How the backend names a custom-field rejection (`field="custom_fields.<key>"`). */
const CUSTOM_FIELD_ERROR_PREFIX = 'custom_fields.';

function normalizeProductType(value: string | null | undefined): QuickCreateFormData['product_type'] | null {
  if (value === 'physical' || value === 'service' || value === 'subscription') return value;
  return null;
}

/** A weight value is only usable as kg when its unit is metric; an imperial value
 * is dropped here (the user re-enters it on the detail page) rather than stored as
 * the wrong magnitude. */
function metricOnly(value: number | null | undefined, unit: string | null | undefined, metricUnit: string): number | undefined {
  if (typeof value !== 'number' || value < 0) return undefined;
  if (unit && unit.toLowerCase() !== metricUnit) return undefined;
  return value;
}

/** A barcode (UPC/GTIN) is only carried into the create payload when it matches the
 * backend digit-count pattern; an AI-extracted value that isn't well-formed (e.g.
 * "N/A") is dropped here rather than 422-ing the whole create. Mirrors the
 * ProductRequest patterns (upc `^\d{6,14}$`, gtin `^\d{8,14}$`). */
function digitsOnly(value: string | null | undefined, minLen: number, maxLen: number): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return new RegExp(`^\\d{${minLen},${maxLen}}$`).test(trimmed) ? trimmed : undefined;
}

export function ProductQuickCreateDialog({
  open,
  onClose,
  onCreated,
  skipNavigation = false,
  defaultName = '',
  defaultRole = 'sell',
  defaultVendorId,
  defaultVendorName,
}: ProductQuickCreateDialogProps) {
  const { t } = useTranslation(['products', 'common']);
  const { t: tcf } = useTranslation('custom_fields');
  const navigate = useNavigate();
  const { prefix } = useEmbeddingPrefix();
  const createMutation = useCreateProduct();
  const createVendorSku = useCreateVendorSku();
  const {
    data: capabilities,
    isError: capabilitiesIsError,
    refetch: refetchCapabilities,
  } = useProductCapabilities();
  const enrichStream = useProgressStream<ProductUrlEnrichment>();

  // Progressive disclosure: open on the "Import from the web" card. Reveal the
  // manual fields once the user skips the lookup or a URL enriches. If seeded with
  // a name (e.g. from picker search), skip straight to manual.
  const [mode, setMode] = useState<'choice' | 'manual'>(defaultName ? 'manual' : 'choice');
  const [urlValue, setUrlValue] = useState('');
  const enrichedFieldsRef = useRef<ProductUrlEnrichment>({});

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<QuickCreateFormData>({
    mode: 'onBlur',
    defaultValues: {
      name: '',
      product_type: 'physical',
      product_role: defaultRole,
      base_unit_name: 'unit',
      base_uom: 'unit',
      category_id: undefined,
    },
  });

  const productRole = watch('product_role');
  const productType = watch('product_type');

  // Product custom fields, REQUIRED ones only. `POST /products` validates
  // `partial=False`, so a tenant's required field is not something the detail page
  // can collect afterwards — without it the create is refused, and this dialog is
  // the only create surface some entry points have. The optional ones stay off the
  // dialog: that is what keeps the quick-create budget honest
  // (.claude/rules/rules-ui-dialogs.md) while the mandatory ones are askable at all.
  // Unlike `company`, a product carries no relationship classes, so the backend
  // scopes nothing here and neither do we.
  const [customFields, setCustomFields] = useState<Record<string, unknown>>({});
  const [customFieldErrors, setCustomFieldErrors] = useState<CustomFieldErrors>({});
  const { data: cfData } = useCustomFieldDefinitions('product');
  const requiredCfDefinitions = useMemo(
    () => activeCustomFieldDefinitions(cfData?.definitions).filter((d) => d.required),
    [cfData?.definitions],
  );

  // A sold product's types are limited by the company profile; a bought
  // ('buy'/'both') product can be any type — every business buys types it never
  // sells. See productTypeOptions for the rule.
  const availableProductTypes = useMemo(
    () => computeAvailableProductTypes(capabilities, productRole),
    [capabilities, productRole],
  );

  // The restriction only actually narrows the list when the product is (purely)
  // sold AND the profile drops some types — that's the case the tooltip explains.
  const typeRestrictedBySell = useMemo(
    () => isSellTypeRestricted(capabilities, productRole),
    [productRole, capabilities],
  );

  // Default type for a fresh open follows the dialog's default role, not the live
  // (possibly stale) watched role.
  const defaultProductType = computeAvailableProductTypes(capabilities, defaultRole)[0] || 'physical';
  // The tenant's own base unit. Required and non-null on the capabilities
  // payload (the route resolves the tenant's value, else 'unit'), so there is no
  // `|| 'unit'` here any more: that fallback is what turned a member's 403 on
  // the admin settings route into "your tenant uses 'unit'", for every tenant
  // that had configured something else.
  const defaultBaseUnitName = capabilities?.default_base_unit_name;
  // `undefined` is TWO states, and conflating them is what turned the fallback
  // fix into a dead end: "not yet" is worth waiting for, "could not" is not.
  // A read that has FAILED gets its own visible, retryable arm below — never a
  // skeleton that outlives the request that would have cleared it.
  const capabilitiesFailed = capabilities === undefined && capabilitiesIsError;
  const capabilitiesPending = capabilities === undefined && !capabilitiesFailed;

  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (!open) {
      prevOpenRef.current = false;
      return;
    }
    if (prevOpenRef.current) return;
    // Seed the form ONCE per open, and only from real values — an open that
    // races the capabilities read waits for it rather than seeding a guess the
    // user would then have to notice and correct. A FAILED read stops waiting
    // and seeds a blank base unit: the create route resolves the tenant's own
    // default for an omitted value, so leaving it empty is how the real setting
    // still gets applied. Writing 'unit' here would be the removed fallback.
    if (capabilitiesPending) return;
    prevOpenRef.current = true;
    reset({
      name: defaultName,
      product_type: defaultProductType,
      product_role: defaultRole,
      base_unit_name: defaultBaseUnitName ?? '',
      // The tenant's default base unit drives the TOKEN field when it spells a
      // catalog token ('bottle', 'kg'); a free-text default keeps the legacy
      // permissive token.
      base_uom: isUomToken(defaultBaseUnitName) ? defaultBaseUnitName : 'unit',
      category_id: undefined,
    });
    setMode(defaultName ? 'manual' : 'choice');
    setUrlValue('');
    enrichedFieldsRef.current = {};
    setCustomFields({});
    setCustomFieldErrors({});
    enrichStream.reset();
  }, [
    open,
    reset,
    defaultProductType,
    defaultBaseUnitName,
    capabilitiesPending,
    defaultName,
    defaultRole,
    enrichStream,
  ]);

  // Apply enrichment to the form exactly once per result.
  const lastSeenRef = useRef<ProductUrlEnrichment | null>(null);
  useEffect(() => {
    const result = enrichStream.data;
    if (!result || result === lastSeenRef.current) return;
    lastSeenRef.current = result;
    setMode('manual');
    if (result.name) setValue('name', result.name, { shouldValidate: true });
    const parsedType = normalizeProductType(result.product_type);
    if (parsedType && availableProductTypes.includes(parsedType)) {
      setValue('product_type', parsedType);
    }
    if (result.base_unit_name) setValue('base_unit_name', result.base_unit_name);
    enrichedFieldsRef.current = result;
  }, [enrichStream.data, setValue, availableProductTypes]);

  // When switching role narrows the type list (e.g. Both → Sell) so the selected
  // type is no longer offered, snap to the first available type.
  useEffect(() => {
    if (!availableProductTypes.includes(productType)) {
      setValue('product_type', availableProductTypes[0]);
    }
  }, [availableProductTypes, productType, setValue]);

  const handleLookup = () => {
    if (!urlValue.trim() || enrichStream.isLoading) return;
    enrichStream.trigger(ENRICH_URL, { url: urlValue.trim() });
  };

  const onSubmit = async (data: QuickCreateFormData) => {
    const enriched = enrichedFieldsRef.current;

    // Before the image upload, deliberately: a submit this refuses creates
    // nothing, so it must not leave an orphaned file behind either.
    const cfErrors = validateCustomFields(requiredCfDefinitions, customFields, tcf);
    setCustomFieldErrors(cfErrors);
    if (Object.keys(cfErrors).length > 0) return;

    // Best-effort: pull the product image down and attach it on create.
    let imageFileId: string | undefined;
    if (enriched.image_url) {
      try {
        const uploaded = await fileApi.uploadFromUrl({
          url: enriched.image_url,
          filename: `${data.name || t('common:entities.product')}-image`,
          description: t('products.importFromUrl.imageDescription', { name: data.name }),
          is_public: true,
          is_user_file: false,
        });
        imageFileId = uploaded.data.id;
      } catch {
        // Non-actionable: the product is created without an image if upload fails.
      }
    }

    try {
      const createData = {
        name: data.name,
        type: data.product_type,
        product_role: data.product_role,
        // Omitted rather than 'unit' when blank: `ProductService.create_product`
        // resolves the TENANT's own default for an absent value, so a client-side
        // literal here would overwrite the very setting it is standing in for —
        // which is exactly what happens on a failed capabilities read, where the
        // field is deliberately seeded empty.
        base_unit_name: data.base_unit_name.trim() || undefined,
        base_uom: data.base_uom,
        product_category_ids: data.category_id ? [data.category_id] : [],
        status: 'active' as const,
        // Preserve the source page as the product's external URL when imported.
        url: urlValue.trim() || undefined,
        sku: enriched.sku || undefined,
        upc: digitsOnly(enriched.upc, 6, 14),
        gtin: digitsOnly(enriched.gtin, 8, 14),
        description: enriched.description || undefined,
        brand: enriched.brand || undefined,
        hs_code: enriched.hs_code || undefined,
        weight_kg: metricOnly(enriched.weight, enriched.weight_unit, 'kg'),
        length_cm: metricOnly(enriched.length, enriched.dimension_unit, 'cm'),
        width_cm: metricOnly(enriched.width, enriched.dimension_unit, 'cm'),
        height_cm: metricOnly(enriched.height, enriched.dimension_unit, 'cm'),
        image_id: imageFileId,
        // A tenant with no required definitions sends exactly the payload it did
        // before this dialog learned about custom fields.
        ...(requiredCfDefinitions.length > 0 ? { custom_fields: customFields } : {}),
      };

      const newProduct = await createMutation.mutateAsync(createData);

      // Seed the Sourcing tab: link the originating vendor as a supplier so a
      // product created from a bill already lists its vendor. Best-effort — the
      // product is created regardless; a failed link surfaces its own toast.
      if (defaultVendorId) {
        try {
          await createVendorSku.mutateAsync({
            productId: newProduct.id,
            data: { vendor_organization_id: defaultVendorId },
          });
        } catch {
          // The global mutation-error handler shows the specific translated toast;
          // the product itself was created successfully.
        }
      }

      onCreated?.(newProduct);
      onClose();

      if (!skipNavigation) {
        navigate(`${prefix}/products/${newProduct.id}`);
      }
    } catch (error) {
      // The global mutation-error handler still shows the specific translated
      // toast — no second local one (docs/design-system/ui-form-errors.md). What
      // it cannot do is place a `custom_fields.<key>` rejection: that names no
      // field of this form, so the message would land nowhere. Route those onto
      // the controls that own them, and keep them OUT of react-hook-form — an
      // error set on a name the form never registered is never cleared by
      // validating the fields it did, so `handleSubmit` would refuse every later
      // submit and Create would silently do nothing for the dialog's whole life.
      const cfErrors: CustomFieldErrors = {};
      for (const [field, message] of Object.entries(getFieldErrors(error) ?? {})) {
        if (field.startsWith(CUSTOM_FIELD_ERROR_PREFIX)) {
          cfErrors[field.slice(CUSTOM_FIELD_ERROR_PREFIX.length)] = message;
        }
      }
      setCustomFieldErrors(cfErrors);
    }
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={t('products.create')}
      onSubmit={mode === 'manual' && !capabilitiesPending ? handleSubmit(onSubmit) : undefined}
      submitLabel={t('common:app.create')}
      isLoading={isSubmitting}
      maxWidth="sm"
    >
      {/* The type list and the base unit are both the tenant's own capability
          facts, so there is nothing honest to render before they arrive — a
          skeleton for the one round trip, never a guessed default. */}
      {capabilitiesPending ? (
        <div className="space-y-4" data-testid="quick-create-capabilities-loading">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : (
      <div className="space-y-4">
        {/* The read FAILED. This dialog is the only create surface some entry
            points have, so it degrades rather than refuses: Create stays live and
            the server — which reads the same settings without this round trip —
            applies the tenant's real base unit and refuses a type it does not
            sell. What must NOT happen is a silent guess, so the notice states the
            cause and offers the retry, the base unit is left blank instead of
            pre-filled, and the Type list is left UNNARROWED rather than narrowed
            by settings nobody read. */}
        {capabilitiesFailed && (
          <Alert data-testid="quick-create-capabilities-error">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('products.capabilities.loadFailedTitle')}</AlertTitle>
            <AlertDescription className="flex flex-col items-start gap-2">
              <span>{t('products.capabilities.loadFailedCreate')}</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => refetchCapabilities()}
              >
                <RefreshCw className="me-2 h-4 w-4" />
                {t('common:app.retry')}
              </Button>
            </AlertDescription>
          </Alert>
        )}
        {mode === 'choice' && (
          <div className="space-y-3 rounded-lg border border-border bg-muted/40 p-4">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 flex-shrink-0 text-primary" />
              <span className="text-sm font-medium text-foreground">
                {t('products.importFromUrl.title')}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">{t('products.importFromUrl.subtitle')}</p>
            <div className="flex gap-2">
              <Input
                value={urlValue}
                onChange={(e) => setUrlValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleLookup();
                  }
                }}
                placeholder={t('products.importFromUrl.placeholder')}
                disabled={enrichStream.isLoading}
                className="flex-1"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleLookup}
                disabled={enrichStream.isLoading || !urlValue.trim()}
                className="flex-shrink-0 gap-1.5"
              >
                {enrichStream.isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                {!enrichStream.isLoading && <span>{t('products.importFromUrl.action')}</span>}
              </Button>
            </div>
            {enrichStream.isLoading && (
              <AIProcessingBanner
                message={enrichStream.message || t('products.importFromUrl.analyzing')}
              />
            )}
            {enrichStream.error && (
              <p className="text-sm text-destructive">{t('products.importFromUrl.failed')}</p>
            )}
            <div className="flex justify-center pt-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  enrichStream.reset();
                  setMode('manual');
                }}
                className="text-muted-foreground touch:min-h-[44px]"
              >
                {t('products.importFromUrl.skip')}
              </Button>
            </div>
          </div>
        )}

        {mode === 'manual' && watch('name') === '' && enrichStream.currentStep !== 'done' && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setMode('choice')}
            className="flex w-full items-center justify-center gap-1.5 border-dashed text-muted-foreground transition hover:border-primary hover:text-foreground touch:min-h-[44px]"
          >
            <Sparkles className="h-4 w-4 text-primary" />
            {t('products.importFromUrl.reopen')}
          </Button>
        )}

        {mode === 'manual' && (
          <>
            <div className="space-y-2">
              <Label htmlFor="name" required>{t('products.name')}</Label>
              <Controller
                name="name"
                control={control}
                rules={{ required: t('common:validation.required') }}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="name"
                    autoFocus
                    className={cn(errors.name && 'border-destructive')}
                  />
                )}
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label>{t('products.role')}</Label>
                <InfoTooltip content={t('products.roleHelp')} />
              </div>
              <Controller
                name="product_role"
                control={control}
                render={({ field }) => (
                  <ToggleGroup
                    type="single"
                    size="lg"
                    value={field.value}
                    onValueChange={(value) => value && field.onChange(value)}
                    className="w-full justify-start bg-muted border border-border rounded-md p-0.5 gap-0"
                  >
                    {PRODUCT_ROLE_VALUES.map((role) => (
                      <ToggleGroupItem
                        key={role}
                        value={role}
                        aria-label={productRoleLabel(t, role)}
                        className="flex-1 rounded-sm px-3 text-muted-foreground data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm"
                      >
                        {productRoleLabel(t, role)}
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                )}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label required>{t('products.type')}</Label>
                <InfoTooltip
                  content={typeRestrictedBySell
                    ? t('products.typeInfoSellRestricted')
                    : t('products.typeInfoAllAvailable')
                  }
                />
              </div>
              <Controller
                name="product_type"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableProductTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {t(`productTypes.${type}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {/* The free-text `base_unit_name` is no longer offered: the catalog
                token below is the unit of record, and two fields answering
                "what is this product's unit?" disagreed on screen (journey
                review). The column stays until Stage 4 retires it. */}
            <Controller
              name="base_uom"
              control={control}
              render={({ field }) => (
                <UomSelect
                  id="base_uom"
                  data-testid="product-base-uom"
                  label={t('products.baseUom')}
                  description={t('products.baseUomInfo')}
                  value={field.value}
                  onChange={(token) => field.onChange(token ?? 'unit')}
                />
              )}
            />

            <div className="space-y-2">
              <Label>{t('products.category')}</Label>
              <Controller
                name="category_id"
                control={control}
                render={({ field }) => (
                  <ProductCategoryPicker
                    value={field.value || ''}
                    onChange={(id) => field.onChange(id || undefined)}
                    allowClear
                  />
                )}
              />
            </div>

            {/* Only the tenant's REQUIRED product custom fields — the create
                endpoint refuses without them, so they are part of the minimum
                this dialog owes; the optional ones stay on the detail page
                (.claude/rules/rules-ui-dialogs.md). */}
            {requiredCfDefinitions.length > 0 && (
              <CustomFieldsSection
                entityType="product"
                requiredOnly
                values={customFields}
                onChange={setCustomFields}
                errors={customFieldErrors}
              />
            )}

            <p className="text-xs text-muted-foreground">
              {t('products.quickCreateHint')}
            </p>

            {defaultVendorName && (
              <p className="text-xs text-muted-foreground">
                {t('products.quickCreateVendorLinkHint', { vendor: defaultVendorName })}
              </p>
            )}
          </>
        )}
      </div>
      )}
    </DialogForm>
  );
}
